import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE, getUserErrorMessage, parseJsonResponse } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import ShopReceiptPaper from './ShopReceiptPaper';

export default function ShopReceiptModal({
  open,
  shopAccountId,
  receiptId,
  shopName,
  city,
  onClose,
  onDeleted,
}) {
  const printRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [receipt, setReceipt] = useState(null);

  const fetchReceipt = useCallback(async () => {
    if (!shopAccountId || !receiptId) return;

    setLoading(true);
    try {
      const response = await authFetch(
        `${API_BASE}/v1/shop/accounts/${shopAccountId}/receipts/${receiptId}`
      );
      const result = await parseJsonResponse(response);
      setReceipt(result.data);
    } catch (error) {
      setReceipt(null);
      toast.error(getUserErrorMessage(error, 'Unable to load receipt.'));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [shopAccountId, receiptId, onClose]);

  useEffect(() => {
    if (!open) {
      setReceipt(null);
      setShowDeleteForm(false);
      setShowDeleteConfirm(false);
      setDeleteReason('');
      return;
    }
    fetchReceipt();
  }, [open, fetchReceipt]);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank', 'width=420,height=720');
    if (!printWindow) {
      toast.error('Pop-up blocked. Allow pop-ups to print the receipt.');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt #${receipt?.invoiceNumber || ''}</title>
          <style>
            body { margin: 0; padding: 12px; font-family: Arial, sans-serif; }
            .sp-receipt-paper { max-width: 80mm; margin: 0 auto; color: #111; }
            .sp-receipt-paper__inner { padding: 8px; }
            .sp-receipt-paper__header { text-align: center; margin-bottom: 10px; }
            .sp-receipt-paper__logo { width: 72px; height: 72px; object-fit: contain; filter: brightness(0); -webkit-filter: brightness(0); print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            @media print {
              .sp-receipt-paper__logo { filter: brightness(0) !important; -webkit-filter: brightness(0) !important; print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; }
            }
            .sp-receipt-paper__brand { margin: 6px 0 2px; font-size: 15px; }
            .sp-receipt-paper__shop { margin: 0; font-size: 11px; color: #444; }
            .sp-receipt-paper__meta, .sp-receipt-paper__customer {
              display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
              border-top: 1px dashed #999; border-bottom: 1px dashed #999;
              padding: 8px 0; margin-bottom: 8px; font-size: 11px;
            }
            .sp-receipt-paper__meta span, .sp-receipt-paper__customer span {
              display: block; color: #666; margin-bottom: 2px;
            }
            .sp-receipt-paper__table { width: 100%; border-collapse: collapse; font-size: 11px; }
            .sp-receipt-paper__table th, .sp-receipt-paper__table td {
              padding: 4px 2px; border-bottom: 1px dashed #ddd; text-align: left;
            }
            .sp-receipt-paper__table th:last-child, .sp-receipt-paper__table td:last-child { text-align: right; }
            .sp-receipt-paper__totals { font-size: 11px; margin-top: 8px; }
            .sp-receipt-paper__totals > div {
              display: flex; justify-content: space-between; padding: 3px 0;
            }
            .sp-receipt-paper__grand {
              border-top: 1px dashed #999; margin-top: 4px; padding-top: 6px !important;
              font-weight: 700;
            }
            .sp-receipt-paper__footer {
              text-align: center; margin-top: 12px; padding-top: 8px;
              border-top: 1px dashed #999; font-size: 11px;
            }
          </style>
        </head>
        <body>${printRef.current.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleDeleteClick = () => {
    const reason = deleteReason.trim();
    if (reason.length < 3) {
      toast.error('Enter a delete reason (at least 3 characters).');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const performDelete = async () => {
    const reason = deleteReason.trim();
    const invoice = receipt?.invoiceNumber || '';

    setDeleting(true);
    try {
      const response = await authFetch(
        `${API_BASE}/v1/shop/accounts/${shopAccountId}/receipts/${receiptId}/delete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      );
      await parseJsonResponse(response);
      setShowDeleteConfirm(false);
      toast.success(`Invoice #${invoice} deleted. Stock fully reversed.`);
      onDeleted?.();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to delete receipt.'));
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="sp-receipt-modal" onClick={onClose} role="presentation">
      <div
        className="sp-receipt-modal__panel"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Receipt details"
      >
        <div className="sp-receipt-modal__head">
          <div>
            <h3 className="sp-receipt-modal__title">Receipt Details</h3>
            <p className="sp-receipt-modal__subtitle">
              {receipt ? `Invoice #${receipt.invoiceNumber}` : 'Loading receipt...'}
            </p>
          </div>
          <button type="button" className="sp-receipt-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="sp-receipt-modal__body">
          {loading ? (
            <p className="sp-muted sp-muted--compact">Loading receipt...</p>
          ) : (
            <div ref={printRef}>
              <ShopReceiptPaper receipt={receipt} shopName={shopName} city={city} />
            </div>
          )}

          {!loading && receipt && showDeleteForm ? (
            <div className="sp-void-form">
              <label htmlFor="sp-delete-reason">Delete reason</label>
              <textarea
                id="sp-delete-reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
                placeholder="e.g. Test invoice / wrong sale / duplicate"
                disabled={deleting}
              />
              <p className="sp-void-form__hint">
                Permanently deletes this receipt. Sale, return and exchange stock will all be reversed.
                This cannot be undone.
              </p>
            </div>
          ) : null}
        </div>

        <div className="sp-receipt-modal__footer">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={onClose} disabled={deleting}>
            Close
          </button>
          {!showDeleteForm ? (
            <button
              type="button"
              className="sp-btn sp-btn--danger"
              onClick={() => setShowDeleteForm(true)}
              disabled={loading || !receipt || deleting}
            >
              Delete Receipt
            </button>
          ) : (
            <>
              <button
                type="button"
                className="sp-btn sp-btn--ghost"
                onClick={() => {
                  setShowDeleteForm(false);
                  setDeleteReason('');
                }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sp-btn sp-btn--danger"
                onClick={handleDeleteClick}
                disabled={deleting || deleteReason.trim().length < 3}
              >
                Confirm Delete
              </button>
            </>
          )}
          <button
            type="button"
            className="sp-btn sp-btn--gold"
            onClick={handlePrint}
            disabled={loading || !receipt || deleting}
          >
            Print Receipt
          </button>
        </div>
      </div>

      <ConfirmDeleteModal
        open={showDeleteConfirm}
        title="Delete Receipt"
        message={
          <>
            Delete invoice <strong>#{receipt?.invoiceNumber || '—'}</strong> permanently?
          </>
        }
        warning="All stock changes (sale, return, exchange) will be reversed. This cannot be undone."
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        loading={deleting}
        onConfirm={performDelete}
        onCancel={() => !deleting && setShowDeleteConfirm(false)}
      />
    </div>
  );
}
